/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Checkbox, ICheckboxStyleProps, ICheckboxStyles } from '@fluentui/react/lib/Checkbox';
import { List } from '@fluentui/react/lib/List';
import { Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction, getId } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { CalloutButton } from 'src/core/components/CalloutButton';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { TestPaneEntityPrediction } from 'src/luis/projects/testPane/components/common/TestPaneEntityPrediction';
import { TestPaneEntityTree } from 'src/luis/projects/testPane/components/common/TestPaneEntityTree';
import { TestPaneSectionLabel } from 'src/luis/projects/testPane/components/common/TestPaneSectionLabel';
import { TestPaneSeparator } from 'src/luis/projects/testPane/components/common/TestPaneSeparator';
import { UtteranceCompositeEntityStore, UtteranceEntityWithTextStore } from 'src/luis/projects/testPane/types/types';

export type EntityInspectorProps = {
    /**
     * The predicted entities per utterance
     */
    predictedEntities: readonly UtteranceEntityWithTextStore[];

    /**
     * The predicted composite entities per utterance
     */
    predictedCompositeEntities: readonly UtteranceCompositeEntityStore[];

    /**
     * The predicted NDepth entities per utterance
     */
    predictedNDepthEntities: readonly UtteranceCompositeEntityStore[];

    /**
     * Flag determines the mode of the inspector whether in change mode or not
     */
    isReadOnly?: boolean;
};

const messages = defineMessages({
    entityTitle: {
        id: 'testPane.entityInspector.topMatchedPattern',
        defaultMessage: 'Other entities'
    },
    compositeEntityTitle: {
        id: 'testPane.entityInspector.compositeEntityTitle',
        defaultMessage: 'Composite entities'
    },
    nDepthEntityTitle: {
        id: 'testPane.entityInspector.nDepthEntityTitle',
        defaultMessage: 'ML entities'
    },
    noPredictions: {
        id: 'testPane.entityInspector.noPredictions',
        defaultMessage: 'No predictions'
    },
    debugRequiredFeatures: {
        id: 'testPane.entityInspector.debugRequiredFeatures',
        defaultMessage: 'Debug required features'
    },
    debugRequiredFeaturesDescription: {
        id: 'testPane.entityInspector.debugRequiredFeaturesDescription',
        defaultMessage: 'Select this option to see what the predictions would be if no entity features were required.'
    }
});

const inspectorTheme = getScopedTheme('EntityInspector');

/**
 * Toggles the collapse state for the given id.
 */
const toggleCollapse = (id: string, expandedIds: string[]) => {
    if (expandedIds.includes(id)) {
        return expandedIds.filter(cKey => cKey !== id);
    }

    return [...expandedIds, id];
};

/**
 * Represents the entity segment in the utterance inspector.
 *
 * @param props The input properties for the inspector.
 */
export const EntityInspector = (props: EntityInspectorProps) => {
    const { predictedEntities, predictedCompositeEntities, predictedNDepthEntities, isReadOnly = false } = props;
    const { localize } = useLocalization();

    const [isSuppressedEnabled, setIsSuppressedEnabled] = React.useState(false);
    const [expandedNDepthIds, setExpandedNDepthIds] = React.useState<string[]>([]);
    const [expandedCompositeIds, setExpandedCompositeIds] = React.useState<string[]>([]);

    const checkboxStyles = classNamesFunction<ICheckboxStyleProps, ICheckboxStyles>()({
        checkbox: { borderColor: !isSuppressedEnabled && `${inspectorTheme.textColor} !important`, marginRight: '8px' }
    });

    const otherEntities = predictedEntities.map(e => ({
        text: e.text,
        name: e.utteranceEntity.role.name.trim().length
            ? `${e.utteranceEntity.entity.name}:${e.utteranceEntity.role.name}`
            : e.utteranceEntity.entity.name
    }));

    const renderComposite = () => {
        if (predictedCompositeEntities.length) {
            return predictedCompositeEntities.map((entity, index) => (
                <Stack key={getId()}>
                    <TestPaneEntityTree
                        entity={entity}
                        isSuppressionDisabled={false}
                        expandedIds={expandedCompositeIds}
                        onExpandingChange={id => setExpandedCompositeIds(toggleCollapse(id, expandedCompositeIds))}
                    />

                    {index !== predictedCompositeEntities.length - 1 && <TestPaneSeparator separatorType="secondarySeparator" />}
                </Stack>
            ));
        }

        return <LuisText>{localize(messages.noPredictions)}</LuisText>;
    };

    const renderNDepth = () => {
        if (predictedNDepthEntities.length) {
            return predictedNDepthEntities
                .filter(entity => isSuppressedEnabled || !entity.parent.isSuppressed)
                .map((entity, index, arr) => (
                    <Stack key={getId()}>
                        <TestPaneEntityTree
                            entity={entity}
                            expandedIds={expandedNDepthIds}
                            isSuppressionDisabled={!isSuppressedEnabled}
                            onExpandingChange={id => setExpandedNDepthIds(toggleCollapse(id, expandedNDepthIds))}
                        />

                        {index !== arr.length - 1 && <TestPaneSeparator separatorType="secondarySeparator" />}
                    </Stack>
                ));
        }

        return <LuisText>{localize(messages.noPredictions)}</LuisText>;
    };

    return (
        <>
            <Stack horizontal horizontalAlign="space-between">
                <TestPaneSectionLabel>{localize(messages.nDepthEntityTitle)}</TestPaneSectionLabel>

                {!isReadOnly && (
                    <Checkbox
                        styles={checkboxStyles}
                        checked={isSuppressedEnabled}
                        onChange={(_, checked) => setIsSuppressedEnabled(checked)}
                        onRenderLabel={() => (
                            <Stack horizontal tokens={{ childrenGap: 1 }}>
                                <LuisText>{localize(messages.debugRequiredFeatures)}</LuisText>

                                <CalloutButton isDarkMode={false} content={localize(messages.debugRequiredFeaturesDescription)} />
                            </Stack>
                        )}
                    />
                )}
            </Stack>

            {renderNDepth()}

            <TestPaneSeparator />

            <TestPaneSectionLabel>{localize(messages.compositeEntityTitle)}</TestPaneSectionLabel>

            {renderComposite()}

            <TestPaneSeparator />

            <TestPaneSectionLabel>{localize(messages.entityTitle)}</TestPaneSectionLabel>

            {predictedEntities.length ? (
                <List
                    items={otherEntities}
                    onRenderCell={item => <TestPaneEntityPrediction entityName={item.name} utteranceSubtext={item.text} />}
                />
            ) : (
                <LuisText>{localize(messages.noPredictions)}</LuisText>
            )}
        </>
    );
};
